import clsx from 'clsx';
import { Fragment, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useSetAtom } from 'jotai';

import { BackTo } from '@/components/back-to';
import { Icon } from '@flintmint/web-shared/components/icon';
import { Divider } from '@flintmint/web-shared/components/divider';

import { emptyLayoutAtom } from '@/state';

import { settingsNavSections } from './settings-nav-config';

import styles from './settings-nav.module.scss';

type SettingsNavProps = {
  title?: string;
};

export const SettingsNav = (props: SettingsNavProps) => {
  const { title = 'Settings & Preferences' } = props;
  const setEmptyLayout = useSetAtom(emptyLayoutAtom);

  useEffect(() => {
    setEmptyLayout(false);
  }, [setEmptyLayout]);

  return (
    <nav className={styles.settingsNav} data-testid="settings-nav" aria-label={title}>
      <BackTo />
      <h1 className={styles.title}>{title}</h1>

      <div className={styles.sections}>
        {settingsNavSections.map((section, index) => (
          <Fragment key={section.id}>
            <section className={styles.section} aria-labelledby={`settings-section-${section.id}`}>
              <h2 id={`settings-section-${section.id}`} className={styles.sectionLabel}>
                {section.label}
              </h2>
              <ul className={styles.itemList}>
                {section.items.map((item) => {
                  const content = (
                    <>
                      <Icon type={item.icon} size={16} />
                      <span className={styles.itemLabel}>{item.label}</span>
                      {item.comingSoon && <span className={styles.comingSoon}>Coming soon</span>}
                      {item.isNew && <span className={styles.newBadge}>New</span>}
                    </>
                  );

                  if (item.comingSoon || !item.to) {
                    return (
                      <li key={item.id}>
                        <span className={clsx(styles.item, styles.disabled)} aria-disabled="true">
                          {content}
                        </span>
                      </li>
                    );
                  }

                  return (
                    <li key={item.id}>
                      <NavLink to={item.to} className={({ isActive }) => clsx(styles.item, { [styles.active]: isActive })}>
                        {content}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </section>
            {index !== settingsNavSections.length - 1 && <Divider orientation="horizontal" size="100%" />}
          </Fragment>
        ))}
      </div>
    </nav>
  );
};
